import { AddPhonemeModal } from "@/components/slides/toolbar/panels/phonics/AddPhonemeModal";
import { TargetIpaPicker } from "@/components/slides/toolbar/panels/phonics/TargetIpaPicker";
import {
  DeletePhonicsAudioInMediaHouseDocument,
  GetPhonemesFromMediaHouseDocument,
  GetPhonicsAudioSpeakerSamplesDocument,
  GetPhonicsAudiosByPhonemeDocument,
  PhonemeType,
  ReorderPhonicsAudioInMediaHouseDocument,
  ScorePhonemeClipDocument,
  type ScorePhonemeClipMutation,
  UpdatePhonemeInMediaHouseDocument,
  UpdatePhonicsAudioInMediaHouseDocument,
  UploadPhonicsAudioToMediaHouseDocument,
  UploadPronunciationAudioDocument,
} from "@/generated/graphql";
import { EmptyState } from "@ai-teach/ui-web";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { MainSidebar } from "../../../components/sidebars/MainSidebar";

type ScoreResult = ScorePhonemeClipMutation["scorePhonemeClip"];

/** Pulls the letter back out of a stored "Speaker A"-style label; null if unlabelled or free-text. */
function speakerLetter(speaker: string | null | undefined): string | null {
  const match = speaker?.match(/^Speaker ([A-Z])$/);
  return match?.[1] ?? null;
}

function speakerLabelForLetter(letter: string): string {
  return `Speaker ${letter}`;
}

/** The edited spelling + target sounds, or null when they match the stored value. */
function buildOverride(
  text: string,
  targetIPA: string[],
  stored: { text: string; targetIPA: string[] } | null | undefined
): { text: string; targetIPA: string[] } | null {
  const t = text.trim();
  if (!t || targetIPA.length === 0) return null;
  if (stored && stored.text === t && stored.targetIPA.join(" ") === targetIPA.join(" "))
    return null;
  return { text: t, targetIPA };
}

const PHONEME_TYPES: { value: "" | PhonemeType; label: string }[] = [
  { value: "", label: "All types" },
  ...Object.entries(PhonemeType).map(([label, value]) => ({ value, label })),
];

function ScoreBadge({ score, passed }: { score: number; passed: boolean }) {
  return (
    <span
      className={`inline-flex min-w-[2.5rem] justify-center rounded-md px-2 py-0.5 text-sm font-bold ${
        passed
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
      }`}
    >
      {score}
    </span>
  );
}

function ResultCard({ result }: { result: ScoreResult }) {
  return (
    <details className="mt-2 rounded-lg border border-gray-200 text-xs dark:border-gray-800">
      <summary className="flex cursor-pointer list-none items-center gap-2 p-3">
        <ScoreBadge score={result.score} passed={result.passed} />
        <span className="text-gray-500">
          /{result.expectedPhoneme}/ → /{result.heardPhoneme || "—"}/ · conf{" "}
          {result.targetConfidence}
        </span>
        <span
          className={`ml-auto ${result.strictScore < 70 ? "text-red-500" : "text-gray-400"}`}
          title="what a real student decode slide would give this clip"
        >
          student: {result.strictScore}
        </span>
      </summary>

      <div className="border-t border-gray-100 p-3 dark:border-gray-800">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
          <span>
            scored against <span className="font-mono">"{result.referenceText}"</span>
          </span>
          <span>recognized: "{result.recognizedText || "—"}"</span>
          <span>
            Azure word: acc {Math.round(result.azureWordAccuracy)} · {result.azureErrorType}
          </span>
          <span>
            {result.producedLetterName && "⚠ letter name  "}
            {result.unscorable && "⚠ unscorable (reference spelling wrong)"}
          </span>
          {result.isolatedFinalSound && (
            <span className="text-sky-600">✂ scored the cropped final segment</span>
          )}
        </div>

        <div className="mt-2 border-t border-gray-100 pt-2 text-gray-500 dark:border-gray-800">
          <div>
            {result.cropInfo} · <span className="font-mono">{result.analyzedDurationMs} ms</span>{" "}
            sent to Azure
          </div>
          {result.analyzedAudioUrl && (
            // biome-ignore lint/a11y/useMediaCaption: dev tool, exact clip sent to Azure
            <audio src={result.analyzedAudioUrl} controls className="mt-1 h-8 w-full" />
          )}
        </div>

        {result.phonemes.length > 0 && (
          <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-800">
            {result.phonemes.map((p, i) => {
              const hasExpected = p.candidates.some((c) => c.phoneme === result.expectedPhoneme);
              return (
                <div key={`${p.phoneme}-${i}`} className="flex gap-3">
                  <span
                    className={`w-16 shrink-0 font-mono ${hasExpected ? "text-emerald-600" : ""}`}
                  >
                    [{i}] /{p.phoneme}/
                  </span>
                  <span className="w-24 shrink-0 font-mono text-gray-500">
                    acc {Math.round(p.accuracyScore)} · {p.durationMs}ms
                  </span>
                  <span className="text-gray-400">
                    {p.candidates
                      .map(
                        (c) =>
                          `${c.phoneme === result.expectedPhoneme ? "▶ " : ""}${c.phoneme} ${Math.round(c.score)}`
                      )
                      .join("    ")}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <ContinuantFallbackTrace json={result.continuantFallbackJson} />

        <details className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-800">
          <summary className="cursor-pointer text-gray-400">raw Azure alignment (JSON)</summary>
          <pre className="mt-1 max-h-72 overflow-auto rounded bg-gray-50 p-2 text-[11px] leading-tight dark:bg-gray-900">
            {prettyJson(result.rawWordsJson)}
          </pre>
        </details>
      </div>
    </details>
  );
}

/** Debug readout of the isolated-continuant fallback decision from the strict (student-path) score. */
function ContinuantFallbackTrace({ json }: { json: string }) {
  let t: Record<string, unknown> | null = null;
  try {
    t = JSON.parse(json) as Record<string, unknown> | null;
  } catch {
    t = null;
  }
  if (!t) {
    return (
      <div className="mt-2 border-t border-gray-100 pt-2 text-gray-400 dark:border-gray-800">
        continuant fallback: n/a (target is not a single continuant)
      </div>
    );
  }
  const fired = t.fired === true;
  return (
    <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-800">
      <div className={fired ? "font-medium text-amber-600" : "text-gray-500"}>
        continuant fallback: {fired ? "FIRED" : `not fired — ${String(t.gateFailed)}`}
      </div>
      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-[11px] text-gray-500">
        <span>target slot: [{String(t.targetSlotIndex)}]</span>
        <span>slot duration: {String(t.targetSlotDurationMs ?? "—")} ms (starved &lt; 40)</span>
        <span>real clip: {String(t.utteranceMs)} ms (needs &gt; 250)</span>
        <span>Azure aligned: {String(t.azureAlignedMs)} ms</span>
        <span>orig target conf: {String(t.originalTargetConfidence)}</span>
        {fired && (
          <>
            <span>
              → carrier slot: [{String(t.carrierSlotIndex)}] /{String(t.carrierSlotTop)}/
            </span>
            <span>carrier target conf: {String(t.carrierTargetConfidence)}</span>
          </>
        )}
      </div>
    </div>
  );
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

interface VcRow {
  label: string;
  word: string;
  strict: number;
  heard: string;
  phonemes: string[];
  unscriptedTargetScore: number;
}

/** Diagnostic: score the target phoneme inside a carrier word ("off") via referenceOverride. */
function VcFrameTest({
  targetId,
  targetGrapheme,
  targetIpa,
}: {
  targetId: string;
  targetGrapheme: string;
  targetIpa: string[];
}) {
  const [uploadAudio] = useMutation(UploadPronunciationAudioDocument);
  const [scoreClip] = useMutation(ScorePhonemeClipDocument);
  const recorder = useRecorder();
  const fileRef = useRef<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [word, setWord] = useState("off");
  const [label, setLabel] = useState("");
  const [rows, setRows] = useState<VcRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear when the phoneme changes — different sound, fresh takes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on phoneme switch
  useEffect(() => {
    setWord("off");
    setLabel("");
    setRows([]);
    setError(null);
    fileRef.current = null;
    setFileName(null);
  }, [targetId]);

  const ipa = targetIpa.length > 0 ? targetIpa : [targetGrapheme];

  const score = async () => {
    const body: Blob | null = fileRef.current ?? recorder.blob.current;
    if (!body || !word.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const up = await uploadAudio({
        variables: { file: new File([body], "vc-frame.wav", { type: "audio/wav" }) },
      });
      const s3Key = up.data?.uploadPronunciationAudio;
      if (!s3Key) throw new Error("Couldn't upload the clip");
      const r = await scoreClip({
        variables: {
          phonemeId: targetId,
          s3Key,
          isolateFinalSound: false,
          deriveUnscriptedRecognition: true,
          referenceOverride: { text: word.trim(), targetIPA: ipa },
        },
      });
      const x = r.data?.scorePhonemeClip;
      if (x) {
        setRows((prev) => [
          ...prev,
          {
            label: label.trim() || `take ${prev.length + 1}`,
            word: word.trim(),
            strict: x.strictScore,
            heard: x.heardPhoneme,
            phonemes: x.phonemes.map((p) => p.phoneme),
            unscriptedTargetScore: x.unscriptedTargetScore,
          },
        ]);
        setLabel("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scoring failed");
    } finally {
      setBusy(false);
    }
  };

  const pass = rows.filter((r) => r.strict >= 70).length;

  return (
    <details
      name="pl-diagnostics"
      className="rounded-lg border border-gray-200 dark:border-gray-800"
    >
      <summary className="cursor-pointer list-none p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
        VC-frame test — say a word, score /{targetGrapheme}/ inside it
        {rows.length > 0 ? ` · ${pass}/${rows.length} ≥ 70` : ""}
      </summary>
      <div className="border-t border-gray-100 p-3 text-xs dark:border-gray-800">
        <p className="mb-2 text-gray-500">
          Record or upload a clip of the word; it's scored in strict (student) mode against that
          word as the reference, still targeting /{ipa.join(" ")}/. A picked file takes priority
          over a recording. Label each take (e.g. "correct 1", "said /p/") so you can eyeball
          whether correct /{targetGrapheme}/ stays ≥ 70 and wrong substitutions stay below.
          Diagnostic only — production scoring is unchanged.
        </p>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            word
            <input
              value={word}
              onChange={(e) => setWord(e.target.value)}
              className="w-24 rounded-md border border-gray-300 bg-white px-2 py-1 font-mono dark:border-gray-700 dark:bg-gray-900"
            />
          </label>
          <label className="flex items-center gap-1">
            label
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="correct 1 / said /p/"
              className="w-40 rounded-md border border-gray-300 bg-white px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
            />
          </label>
          <button
            type="button"
            onClick={recorder.recording ? recorder.stop : recorder.start}
            className={`rounded-md px-3 py-1.5 text-white ${
              recorder.recording ? "bg-red-600 hover:bg-red-700" : "bg-gray-700 hover:bg-gray-800"
            }`}
          >
            {recorder.recording ? "Stop" : "Record"}
          </button>
          <label className="cursor-pointer rounded-md bg-gray-700 px-3 py-1.5 text-white hover:bg-gray-800">
            Upload
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                fileRef.current = f;
                setFileName(f?.name ?? null);
                e.target.value = "";
              }}
            />
          </label>
          {fileName ? (
            <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
              file: <span className="font-mono">{fileName}</span>
              <button
                type="button"
                onClick={() => {
                  fileRef.current = null;
                  setFileName(null);
                }}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </span>
          ) : (
            recorder.blobUrl && (
              // biome-ignore lint/a11y/useMediaCaption: dev tool, own recording
              <audio src={recorder.blobUrl} controls className="h-8" />
            )
          )}
          <button
            type="button"
            onClick={score}
            disabled={(!recorder.blobUrl && !fileName) || busy}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? "Scoring…" : `Score take${fileName ? " (file)" : ""}`}
          </button>
        </div>
        {error && <p className="mb-2 text-red-500">{error}</p>}

        {rows.length > 0 && (
          <>
            <div className="mb-1 font-mono text-[11px] text-gray-600">
              {pass}/{rows.length} takes ≥ 70
            </div>
            <div className="max-h-80 overflow-auto rounded border border-gray-100 dark:border-gray-800">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-gray-50 text-left dark:bg-gray-900">
                  <tr>
                    <th className="px-2 py-1">label</th>
                    <th className="px-2 py-1">word</th>
                    <th className="px-2 py-1">strict</th>
                    <th className="px-2 py-1">≥ 70</th>
                    <th className="px-2 py-1">heard</th>
                    <th className="px-2 py-1">phonemes</th>
                    <th className="px-2 py-1">unscripted /{targetGrapheme}/</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={`${r.label}-${i}`}
                      className={r.strict >= 70 ? "bg-emerald-50 dark:bg-emerald-900/20" : ""}
                    >
                      <td className="px-2 py-1">{r.label}</td>
                      <td className="px-2 py-1 font-mono">{r.word}</td>
                      <td
                        className={`px-2 py-1 font-mono font-semibold ${
                          r.strict >= 70 ? "text-emerald-600" : "text-red-600"
                        }`}
                      >
                        {r.strict}
                      </td>
                      <td className="px-2 py-1">{r.strict >= 70 ? "yes" : "no"}</td>
                      <td className="px-2 py-1 font-mono">/{r.heard || "—"}/</td>
                      <td className="px-2 py-1 font-mono text-gray-400">
                        {r.phonemes.length ? `/${r.phonemes.join(" ")}/` : "—"}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-400">
                        {r.unscriptedTargetScore < 0 ? "—" : Math.round(r.unscriptedTargetScore)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </details>
  );
}

interface ProbeSide {
  strictScore: number;
  /** Best n-best score for the target in open-ended recognition; -1 if absent. */
  unscriptedTargetScore: number;
  unscriptedText: string;
}
interface ProbeRow {
  probe: string;
  clip: string;
  /** dB of peak-normalization gain the ON pass applied (0 = clip already loud enough). */
  gainDb: number;
  raw: ProbeSide;
  norm: ProbeSide;
}

/** Diagnostic: score the ticked phonemes' clips against the target, strict + open-ended, raw + normalized. */
function RecognitionProbe({
  targetId,
  targetGrapheme,
  candidates,
}: {
  targetId: string;
  targetGrapheme: string;
  candidates: { id: string; phoneme: string; phonemeType: string; audioCount: number }[];
}) {
  const client = useApolloClient();
  const [scoreClip] = useMutation(ScorePhonemeClipDocument);
  // Pre-tick the target itself — its own reference clips are the positive control.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set([targetId]));
  const [rows, setRows] = useState<ProbeRow[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  // Reset when the target phoneme changes — re-tick just the new target, clear old results.
  useEffect(() => {
    setSelected(new Set([targetId]));
    setRows([]);
    setRunning(false);
    setDone(false);
  }, [targetId]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const probeOne = async (
    url: string,
    normalizeAudio: boolean
  ): Promise<(ProbeSide & { gainDb: number }) | null> => {
    const r = await scoreClip({
      variables: {
        phonemeId: targetId,
        audioUrl: url,
        isolateFinalSound: false,
        deriveUnscriptedRecognition: true,
        normalizeAudio,
      },
    });
    const s = r.data?.scorePhonemeClip;
    if (!s) return null;
    return {
      strictScore: s.strictScore,
      unscriptedTargetScore: s.unscriptedTargetScore,
      unscriptedText: s.unscriptedText,
      gainDb: s.normalizationGainDb,
    };
  };

  const run = async () => {
    setRunning(true);
    setDone(false);
    setRows([]);
    const probes = candidates.filter((c) => selected.has(c.id));
    for (const probe of probes) {
      let audios: { url: string; label: string; voiceVariant: number }[] = [];
      try {
        const res = await client.query({
          query: GetPhonicsAudiosByPhonemeDocument,
          variables: { phonemeId: probe.id },
          fetchPolicy: "network-only",
        });
        audios = res.data?.phonicsAudiosByPhonemeFromMediaHouse ?? [];
      } catch {
        continue;
      }
      for (const a of audios) {
        try {
          const raw = await probeOne(a.url, false);
          const norm = await probeOne(a.url, true);
          if (!raw || !norm) continue;
          setRows((prev) => [
            ...prev,
            {
              probe: probe.phoneme,
              clip: a.label || `voice ${a.voiceVariant}`,
              gainDb: norm.gainDb,
              raw,
              norm,
            },
          ]);
        } catch {
          // skip clips Azure couldn't process
        }
      }
    }
    setRunning(false);
    setDone(true);
  };

  const bySound = [...new Set(rows.map((r) => r.probe))].map((sound) => {
    const group = rows.filter((r) => r.probe === sound);
    const agg = (side: (r: ProbeRow) => ProbeSide) => ({
      passed: group.filter((r) => side(r).strictScore >= 70).length,
      heard: group.filter((r) => side(r).unscriptedTargetScore >= 0).length,
      maxStrict: Math.max(0, ...group.map((r) => side(r).strictScore)),
    });
    return { sound, total: group.length, raw: agg((r) => r.raw), norm: agg((r) => r.norm) };
  });

  return (
    <details
      name="pl-diagnostics"
      className="rounded-lg border border-gray-200 dark:border-gray-800"
    >
      <summary className="cursor-pointer list-none p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
        Recognition probe — reference clips scored against /{targetGrapheme}/
      </summary>
      <div className="border-t border-gray-100 p-3 text-xs dark:border-gray-800">
        <p className="mb-2 text-gray-500">
          Scores every reference recording of the ticked phonemes against /{targetGrapheme}/, in
          strict (student) mode plus open-ended recognition, each done <strong>twice</strong> —
          peak-normalization OFF then ON. Columns show <span className="font-mono">raw → norm</span>
          . Only ever boosts toward −1 dBFS, so an already-loud clip shows 0 dB gain and identical
          raw/norm. The /{targetGrapheme}/ chip is pre-ticked (positive control — the 0/34 starved
          clips are here); also probe silence, /h/, /s/, /v/, /th/, vowels to confirm normalization
          does <em>not</em> make them start showing /{targetGrapheme}/. Diagnostic only.
        </p>
        <div className="mb-2 flex flex-wrap gap-1">
          {candidates.map((c) => {
            const isTarget = c.id === targetId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                  selected.has(c.id)
                    ? isTarget
                      ? "bg-emerald-600 text-white"
                      : "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                }`}
                title={isTarget ? "target — positive control" : c.phonemeType}
              >
                /{c.phoneme}/ {c.audioCount}
                {isTarget ? " ✓" : ""}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running || selected.size === 0}
          className="rounded-md bg-gray-700 px-3 py-1.5 text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {running ? `Probing… (${rows.length})` : "Run probe"}
        </button>

        {bySound.length > 0 && (
          <div className="mt-3 space-y-0.5 font-mono text-[11px]">
            {bySound.map((s) => (
              <div
                key={s.sound}
                className={
                  s.norm.heard > s.raw.heard || s.norm.passed > s.raw.passed ? "text-amber-600" : ""
                }
              >
                /{s.sound}/: strict ≥ 70 {s.raw.passed}→{s.norm.passed} / {s.total} · heard /
                {targetGrapheme}/ {s.raw.heard}→{s.norm.heard} / {s.total} · max strict{" "}
                {s.raw.maxStrict}→{s.norm.maxStrict}
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-2 max-h-80 overflow-auto rounded border border-gray-100 dark:border-gray-800">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-gray-50 text-left dark:bg-gray-900">
                <tr>
                  <th className="px-2 py-1">probe</th>
                  <th className="px-2 py-1">clip</th>
                  <th className="px-2 py-1">gain</th>
                  <th className="px-2 py-1">strict raw→norm</th>
                  <th className="px-2 py-1">/{targetGrapheme}/ n-best raw→norm</th>
                  <th className="px-2 py-1">unscripted text raw→norm</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const uts = (n: number) => (n < 0 ? "—" : String(Math.round(n)));
                  const rescued = r.norm.strictScore >= 70 && r.raw.strictScore < 70;
                  return (
                    <tr
                      key={`${r.probe}-${r.clip}-${i}`}
                      className={
                        rescued || r.norm.unscriptedTargetScore > r.raw.unscriptedTargetScore
                          ? "bg-amber-50 dark:bg-amber-900/20"
                          : ""
                      }
                    >
                      <td className="px-2 py-1 font-mono">/{r.probe}/</td>
                      <td className="px-2 py-1">{r.clip}</td>
                      <td className="px-2 py-1 font-mono text-gray-400">
                        {r.gainDb > 0 ? `+${r.gainDb.toFixed(1)}` : "0"} dB
                      </td>
                      <td
                        className={`px-2 py-1 font-mono ${rescued ? "font-semibold text-emerald-600" : ""}`}
                      >
                        {r.raw.strictScore} → {r.norm.strictScore}
                      </td>
                      <td className="px-2 py-1 font-mono">
                        {uts(r.raw.unscriptedTargetScore)} → {uts(r.norm.unscriptedTargetScore)}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-400">
                        "{r.raw.unscriptedText || "—"}" → "{r.norm.unscriptedText || "—"}"
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {done && rows.length === 0 && (
          <p className="mt-2 text-gray-500">No clips scored (no audio, or Azure rejected them).</p>
        )}
      </div>
    </details>
  );
}

/** POST the recorded blob to S3 via a pre-signed URL, then hand back the s3Key to score against. */
function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      blobRef.current = blob;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      for (const track of stream.getTracks()) track.stop();
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  }, []);

  const stop = useCallback(() => {
    recorderRef.current?.stop();
    setRecording(false);
  }, []);

  const reset = useCallback(() => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    chunksRef.current = [];
    blobRef.current = null;
    setRecording(false);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  return { recording, blobUrl, blob: blobRef, start, stop, reset };
}

type ReviewStatus = "done" | "problem";
interface ReviewEntry {
  status?: ReviewStatus;
  note?: string;
}
const REVIEW_KEY = "phoneme-lab-review-v1";

/** Per-phoneme review progress, kept only in this browser (localStorage). */
function useReviewStatus() {
  const [map, setMap] = useState<Record<string, ReviewEntry>>(() => {
    try {
      return JSON.parse(localStorage.getItem(REVIEW_KEY) ?? "{}");
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(REVIEW_KEY, JSON.stringify(map));
    } catch {
      // storage disabled — progress just won't persist
    }
  }, [map]);

  const write = useCallback(
    (id: string, patch: { status?: ReviewStatus | undefined; note?: string | undefined }) => {
      setMap((prev) => {
        const status = "status" in patch ? patch.status : prev[id]?.status;
        // Keep the note exactly as typed — trimming here eats spaces the moment they're pressed.
        const rawNote = "note" in patch ? patch.note : prev[id]?.note;
        const note = rawNote && rawNote.length > 0 ? rawNote : undefined;
        const next = { ...prev };
        if (!status && !note) delete next[id];
        else next[id] = { ...(status ? { status } : {}), ...(note ? { note } : {}) };
        return next;
      });
    },
    []
  );

  const cycleStatus = useCallback((id: string) => {
    setMap((prev) => {
      const cur = prev[id]?.status;
      const status: ReviewStatus | undefined =
        cur === undefined ? "done" : cur === "done" ? "problem" : undefined;
      const note = prev[id]?.note;
      const next = { ...prev };
      if (!status && !note) delete next[id];
      else next[id] = { ...(status ? { status } : {}), ...(note ? { note } : {}) };
      return next;
    });
  }, []);

  return { map, write, cycleStatus };
}

const STATUS_DOT: Record<"none" | ReviewStatus, string> = {
  none: "border-l-transparent",
  done: "border-l-emerald-500",
  problem: "border-l-amber-500",
};

function PhonemeLabPage() {
  const [phonemeType, setPhonemeType] = useState<"" | PhonemeType>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resultsByAudioId, setResultsByAudioId] = useState<Record<string, ScoreResult>>({});
  const [ownVoiceResult, setOwnVoiceResult] = useState<ScoreResult | null>(null);
  // Several clips can score at once, so track every in-flight key rather than a single one.
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const startBusy = (key: string) => setBusy((prev) => new Set(prev).add(key));
  const endBusy = (key: string) =>
    setBusy((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  const [error, setError] = useState<string | null>(null);

  // Ad-hoc clip: score it, and keep it only if the admin saves it as a reference recording.
  const uploadFileRef = useRef<File | null>(null);
  const speakerPreviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [uploadClipUrl, setUploadClipUrl] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<ScoreResult | null>(null);
  const [uploadSaved, setUploadSaved] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);

  const phonemesQuery = useQuery(GetPhonemesFromMediaHouseDocument, {
    variables: phonemeType ? { phonemeType } : {},
    fetchPolicy: "cache-and-network",
  });
  const audiosQuery = useQuery(GetPhonicsAudiosByPhonemeDocument, {
    variables: { phonemeId: selectedId ?? "" },
    skip: !selectedId,
    fetchPolicy: "cache-and-network",
  });
  const speakersQuery = useQuery(GetPhonicsAudioSpeakerSamplesDocument, {
    fetchPolicy: "cache-and-network",
  });

  const [uploadAudio] = useMutation(UploadPronunciationAudioDocument);
  const [scoreClip] = useMutation(ScorePhonemeClipDocument);
  const [updatePhoneme, { loading: saving }] = useMutation(UpdatePhonemeInMediaHouseDocument);
  const [uploadReferenceAudio, { loading: uploadingReference }] = useMutation(
    UploadPhonicsAudioToMediaHouseDocument
  );
  const [deletePhonicsAudio] = useMutation(DeletePhonicsAudioInMediaHouseDocument);
  const [reorderPhonicsAudio] = useMutation(ReorderPhonicsAudioInMediaHouseDocument);
  const [updatePhonicsAudio] = useMutation(UpdatePhonicsAudioInMediaHouseDocument);

  const recorder = useRecorder();
  const review = useReviewStatus();
  const [listFilter, setListFilter] = useState<
    "all" | "audio" | "no-audio" | "unreviewed" | "done" | "problem"
  >("all");
  const [search, setSearch] = useState("");
  const [showAddPhoneme, setShowAddPhoneme] = useState(false);

  const phonemes = phonemesQuery.data?.phonemesFromMediaHouse ?? [];
  const selected = phonemes.find((p) => p.id === selectedId) ?? null;
  const audios = audiosQuery.data?.phonicsAudiosByPhonemeFromMediaHouse ?? [];
  const [unlabelledOnly, setUnlabelledOnly] = useState(false);
  const visibleAudios = unlabelledOnly ? audios.filter((a) => !a.speaker) : audios;

  // Chip pool covers every letter used anywhere, plus extra letters the "+" button revealed this session.
  const [extraSpeakerLetters, setExtraSpeakerLetters] = useState(0);
  const speakerSamples = speakersQuery.data?.phonicsAudioSpeakerSamplesFromMediaHouse ?? [];
  // Letter -> a clip already tagged with that speaker, so it can be previewed before assigning.
  const speakerSampleUrlByLetter = new Map(
    speakerSamples
      .map((s): [string, string] | null => {
        const letter = speakerLetter(s.speaker);
        return letter ? [letter, s.audio.url] : null;
      })
      .filter((entry): entry is [string, string] => entry !== null)
  );
  const usedSpeakerLetters = new Set(speakerSampleUrlByLetter.keys());
  const maxUsedLetterIndex =
    usedSpeakerLetters.size > 0
      ? Math.max(...[...usedSpeakerLetters].map((l) => l.charCodeAt(0) - 65))
      : -1;
  const speakerLetterPoolSize = Math.min(
    26,
    Math.max(maxUsedLetterIndex + 1, 1) + extraSpeakerLetters
  );
  const speakerLetterPool = Array.from({ length: speakerLetterPoolSize }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  // Same order the API/mobile default to: phonemeType, then phoneme text.
  const sortedPhonemes = [...phonemes].sort((a, b) => {
    const diff = a.phonemeType.localeCompare(b.phonemeType);
    return diff !== 0 ? diff : a.phoneme.localeCompare(b.phoneme);
  });
  const visiblePhonemes = sortedPhonemes.filter((p) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${p.phoneme} ${p.azureReference?.text ?? ""} ${p.tags.join(" ")}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    const st = review.map[p.id]?.status;
    if (listFilter === "audio") return p.audioCount > 0;
    if (listFilter === "no-audio") return p.audioCount === 0;
    if (listFilter === "unreviewed") return !st;
    if (listFilter === "done") return st === "done";
    if (listFilter === "problem") return st === "problem";
    return true;
  });
  const counts = {
    total: phonemes.length,
    withAudio: phonemes.filter((p) => p.audioCount > 0).length,
    noAudio: phonemes.filter((p) => p.audioCount === 0).length,
    done: phonemes.filter((p) => review.map[p.id]?.status === "done").length,
    problem: phonemes.filter((p) => review.map[p.id]?.status === "problem").length,
  };

  // Trial reference — seeded from the record, sent as an override so it can be scored before saving.
  const [refText, setRefText] = useState("");
  const [refIPA, setRefIPA] = useState<string[]>([]);
  const storedText = selected?.azureReference?.text ?? "";
  const storedIPA = selected?.azureReference?.targetIPA.join(" ") ?? "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedId is a deliberate re-seed trigger
  useEffect(() => {
    setRefText(storedText);
    setRefIPA(storedIPA ? storedIPA.split(" ") : []);
  }, [selectedId, storedText, storedIPA]);

  const override = buildOverride(refText, refIPA, selected?.azureReference);
  const referenceOverride = override ?? undefined;

  const saveReference = async () => {
    if (!selectedId || !override || !selected) return;
    setError(null);
    try {
      // Re-send the whole record so Media House can't drop the fields we're not editing.
      await updatePhoneme({
        variables: {
          input: {
            id: selectedId,
            phoneme: selected.phoneme,
            phonemeType: selected.phonemeType,
            tags: selected.tags,
            ...(selected.arpabet
              ? {
                  arpabet: {
                    sequence: selected.arpabet.sequence,
                    targetIndex: selected.arpabet.targetIndex,
                  },
                }
              : {}),
            ...(selected.hint != null ? { hint: selected.hint } : {}),
            azureReference: override,
          },
        },
      });
      await phonemesQuery.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the reference");
    }
  };

  // Push a blob through the API and hand back its s3Key.
  const putToS3 = async (body: Blob): Promise<string> => {
    const upload = await uploadAudio({
      variables: { file: new File([body], "phoneme-lab.wav", { type: "audio/wav" }) },
    });
    const s3Key = upload.data?.uploadPronunciationAudio;
    if (!s3Key) throw new Error("Couldn't upload the clip");
    return s3Key;
  };

  const scoreExisting = async (audioId: string, audioUrl: string) => {
    if (!selectedId) return;
    startBusy(audioId);
    setError(null);
    try {
      const res = await scoreClip({
        variables: { phonemeId: selectedId, audioUrl, referenceOverride, isolateFinalSound: true },
      });
      const scored = res.data?.scorePhonemeClip;
      if (scored) setResultsByAudioId((prev) => ({ ...prev, [audioId]: scored }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scoring failed");
    } finally {
      endBusy(audioId);
    }
  };

  const removeAudio = async (audioId: string) => {
    if (!selectedId) return;
    if (!window.confirm("Move this reference clip to trash? You can restore it from Media House."))
      return;
    startBusy(audioId);
    setError(null);
    try {
      await deletePhonicsAudio({ variables: { id: audioId } });
      setResultsByAudioId((prev) => {
        const next = { ...prev };
        delete next[audioId];
        return next;
      });
      await Promise.all([audiosQuery.refetch(), phonemesQuery.refetch()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove that clip");
    } finally {
      endBusy(audioId);
    }
  };

  const moveAudio = async (audioId: string, direction: "up" | "down") => {
    if (!selectedId) return;
    const idx = audios.findIndex((a) => a.id === audioId);
    if (idx === -1) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= audios.length) return;
    const orderedIds = audios.map((a) => a.id);
    const tmp = orderedIds[idx];
    orderedIds[idx] = orderedIds[swapIdx] as string;
    orderedIds[swapIdx] = tmp as string;
    try {
      await reorderPhonicsAudio({ variables: { phonemeId: selectedId, orderedIds } });
      await audiosQuery.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't reorder that clip");
    }
  };

  const setAudioSpeaker = async (audioId: string, speaker: string) => {
    try {
      await updatePhonicsAudio({ variables: { id: audioId, speaker: speaker.trim() } });
      await Promise.all([audiosQuery.refetch(), speakersQuery.refetch()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save that speaker label");
    }
  };

  const setAudioLabel = async (audioId: string, label: "card" | "sound") => {
    try {
      await updatePhonicsAudio({ variables: { id: audioId, label } });
      await audiosQuery.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save that card/sound label");
    }
  };

  /** Plays a sample clip so a reviewer can hear what a speaker letter sounds like without leaving the current phoneme. */
  const playSpeakerSample = (url: string) => {
    if (!speakerPreviewAudioRef.current) {
      speakerPreviewAudioRef.current = new window.Audio();
    }
    const player = speakerPreviewAudioRef.current;
    player.src = url;
    player.currentTime = 0;
    player.play().catch(() => {});
  };

  /** Clicking an already-selected letter clears it; any other letter selects it. */
  const toggleAudioSpeakerLetter = (
    audioId: string,
    currentSpeaker: string | null | undefined,
    letter: string
  ) => {
    const isSelected = speakerLetter(currentSpeaker) === letter;
    setAudioSpeaker(audioId, isSelected ? "" : speakerLabelForLetter(letter));
  };

  const scoreOwnVoice = async () => {
    if (!selectedId || !recorder.blob.current) return;
    startBusy("own-voice");
    setError(null);
    try {
      // Own voice mimics a student — no letter-name trimming.
      const s3Key = await putToS3(recorder.blob.current);
      const res = await scoreClip({
        variables: { phonemeId: selectedId, s3Key, referenceOverride },
      });
      if (res.data?.scorePhonemeClip) setOwnVoiceResult(res.data.scorePhonemeClip);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scoring failed");
    } finally {
      endBusy("own-voice");
    }
  };

  const clearUpload = () => {
    setUploadClipUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    uploadFileRef.current = null;
    setUploadResult(null);
    setUploadSaved(false);
  };

  const pickUpload = (file: File) => {
    clearUpload();
    uploadFileRef.current = file;
    setUploadClipUrl(URL.createObjectURL(file));
  };

  const dropUpload = (e: React.DragEvent) => {
    e.preventDefault();
    setUploadDragOver(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("audio/"));
    if (file) pickUpload(file);
    else if (e.dataTransfer.files.length > 0) setError("Drop an audio file.");
  };

  const scoreUpload = async () => {
    if (!selectedId || !uploadFileRef.current) return;
    startBusy("upload");
    setError(null);
    try {
      const s3Key = await putToS3(uploadFileRef.current);
      const res = await scoreClip({
        variables: { phonemeId: selectedId, s3Key, referenceOverride, isolateFinalSound: true },
      });
      if (res.data?.scorePhonemeClip) setUploadResult(res.data.scorePhonemeClip);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scoring failed");
    } finally {
      endBusy("upload");
    }
  };

  const saveUploadAsReference = async () => {
    if (!selectedId || !uploadFileRef.current) return;
    setError(null);
    try {
      await uploadReferenceAudio({
        variables: { file: uploadFileRef.current, phonemeId: selectedId },
      });
      await Promise.all([audiosQuery.refetch(), phonemesQuery.refetch()]);
      setUploadSaved(true);
      uploadFileRef.current = null;
      setUploadClipUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save that as a reference audio");
    }
  };

  const selectPhoneme = (id: string) => {
    setSelectedId(id);
    setResultsByAudioId({});
    setOwnVoiceResult(null);
    clearUpload();
    recorder.reset();
    setError(null);
  };

  return (
    <div className="flex h-screen">
      <MainSidebar />
      <div className="flex flex-1 flex-col overflow-hidden p-6">
        <div className="mb-4 shrink-0">
          <h1 className="h3">Phoneme Scoring Lab</h1>
          <p className="text-sm text-gray-500">
            Score audio against a phoneme's Azure reference — the exact number a decode slide would
            give. Tune each phoneme's reference spelling + target sound on its Media House record.
          </p>
        </div>

        {error && <p className="mb-3 shrink-0 text-sm text-red-500">{error}</p>}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[300px_1fr]">
          {/* Phoneme picker — only this column scrolls */}
          <div className="flex min-h-0 flex-col gap-2">
            <div className="flex shrink-0 gap-2">
              <select
                value={phonemeType}
                onChange={(e) => {
                  setPhonemeType(e.target.value as "" | PhonemeType);
                  setSelectedId(null);
                  recorder.reset();
                }}
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
              >
                {PHONEME_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowAddPhoneme(true)}
                title="Add a new phoneme"
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700"
              >
                + Add
              </button>
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search phonemes…"
              className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
            />

            <div className="shrink-0 text-[11px] text-gray-500">
              {counts.total} phonemes · {counts.withAudio} with audio ·{" "}
              <span className="text-sky-600">{counts.noAudio} no audio</span> ·{" "}
              <span className="text-emerald-600">{counts.done} done</span> ·{" "}
              <span className="text-amber-600">{counts.problem} problem</span>
            </div>
            <div className="flex shrink-0 flex-wrap gap-1">
              {(["all", "audio", "no-audio", "unreviewed", "done", "problem"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setListFilter(f)}
                  className={`rounded px-2 py-0.5 text-[11px] capitalize ${
                    listFilter === f
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                  }`}
                >
                  {f === "audio" ? "with audio" : f === "no-audio" ? "no audio" : f}
                </button>
              ))}
            </div>

            {phonemesQuery.loading && phonemes.length === 0 ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : (
              <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
                {visiblePhonemes.map((p) => {
                  const st = review.map[p.id]?.status ?? "none";
                  return (
                    <div
                      key={p.id}
                      className={`flex items-start gap-2 rounded-lg border border-l-4 px-3 py-2 text-sm transition-colors ${
                        STATUS_DOT[st]
                      } ${
                        selectedId === p.id
                          ? "border-primary-400 bg-primary-50/60 dark:bg-primary-900/20"
                          : "border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => review.cycleStatus(p.id)}
                        title="Cycle: unreviewed → done → problem"
                        className="mt-0.5 text-sm leading-none"
                      >
                        {st === "done" ? "✅" : st === "problem" ? "⚠️" : "⬜"}
                      </button>
                      <button
                        type="button"
                        onClick={() => selectPhoneme(p.id)}
                        className="flex-1 text-left"
                      >
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {p.phoneme}
                        </span>{" "}
                        <span className="text-xs text-gray-400">{p.phonemeType}</span>
                        {p.audioCount > 0 && (
                          <span className="ml-1 text-[11px] text-sky-600">🔊 {p.audioCount}</span>
                        )}
                        <div className="text-[11px] text-gray-500">
                          {p.azureReference
                            ? `"${p.azureReference.text}" → /${p.azureReference.targetIPA.join(" ")}/`
                            : "no reference — uses in-code fallback"}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Scoring — fixed column, scrolls within itself */}
          <div className="min-h-0 overflow-y-auto">
            {!selected ? (
              <EmptyState
                title="Pick a phoneme"
                description="Then score its reference audios, or record your own voice."
              />
            ) : (
              <div className="flex flex-col gap-6">
                {/* Review status + note (this browser only) */}
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                  {(["done", "problem"] as const).map((s) => {
                    const active = review.map[selected.id]?.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          review.write(selected.id, { status: active ? undefined : s })
                        }
                        className={`rounded px-2 py-1 text-xs capitalize ${
                          active
                            ? s === "done"
                              ? "bg-emerald-600 text-white"
                              : "bg-amber-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                        }`}
                      >
                        {s === "done" ? "✅ done" : "⚠️ problem"}
                      </button>
                    );
                  })}
                  <input
                    value={review.map[selected.id]?.note ?? ""}
                    onChange={(e) => review.write(selected.id, { note: e.target.value })}
                    placeholder="note (e.g. derives /d/, spelling needs fixing)"
                    className="min-w-[240px] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>

                {/* Reference (spelling + target sound) — trial via override, then save */}
                <section>
                  <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Reference — {selected.phoneme}
                  </h2>
                  <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                    <div className="flex flex-wrap items-end gap-4">
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="text-gray-500">Azure reference spelling</span>
                        <input
                          value={refText}
                          onChange={(e) => setRefText(e.target.value)}
                          placeholder="buh"
                          className="w-40 rounded-md border border-gray-300 bg-white px-2 py-1 font-mono text-sm dark:border-gray-700 dark:bg-gray-900"
                        />
                      </label>
                      <div className="min-w-[260px] flex-1">
                        <TargetIpaPicker value={refIPA} onChange={setRefIPA} idPrefix="lab" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={saveReference}
                        disabled={!override || saving}
                        className="rounded-md bg-primary-600 px-3 py-1.5 text-xs text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {saving ? "Saving…" : "Save to phoneme record"}
                      </button>
                      <span className="text-[11px] text-gray-400">
                        {override
                          ? "unsaved — scores below use this override"
                          : selected.azureReference
                            ? "matches the saved reference"
                            : "no saved reference — scores use the in-code fallback"}
                      </span>
                    </div>
                  </div>
                </section>

                {/* Existing audio */}
                <section>
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Reference audio ({visibleAudios.length}
                      {unlabelledOnly ? ` of ${audios.length}` : ""})
                    </h2>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={unlabelledOnly}
                        onChange={(e) => setUnlabelledOnly(e.target.checked)}
                      />
                      unlabelled only
                    </label>
                  </div>
                  {audiosQuery.loading && audios.length === 0 ? (
                    <p className="text-sm text-gray-500">Loading…</p>
                  ) : audios.length === 0 ? (
                    <p className="text-sm text-gray-500">No reference audio for this phoneme.</p>
                  ) : visibleAudios.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Every clip for this phoneme already has a speaker label.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {visibleAudios.map((a) => {
                        const audioResult = resultsByAudioId[a.id];
                        // Up/down disabled state checks position in the FULL list, not the filtered one.
                        const fullIdx = audios.findIndex((x) => x.id === a.id);
                        return (
                          <div
                            key={a.id}
                            className="rounded-lg border border-gray-200 p-3 dark:border-gray-800"
                          >
                            {/* Player on its own top row — always reachable/visible, independent
                                of however wide the speaker-chip row below ends up. */}
                            <div className="flex items-center gap-3">
                              {/* Reorder — position across all phonemes is what lets a reviewer
                                  line up "slot 1 = same speaker" without relying on upload order alone. */}
                              <div className="flex flex-col">
                                <button
                                  type="button"
                                  onClick={() => moveAudio(a.id, "up")}
                                  disabled={fullIdx === 0}
                                  aria-label="Move up"
                                  className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveAudio(a.id, "down")}
                                  disabled={fullIdx === audios.length - 1}
                                  aria-label="Move down"
                                  className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
                                >
                                  ▼
                                </button>
                              </div>
                              <span className="text-sm font-medium">voice {a.voiceVariant}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setAudioLabel(a.id, a.label === "card" ? "sound" : "card")
                                }
                                title="Click to toggle between card and sound"
                                className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${
                                  a.label === "card"
                                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                    : "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
                                }`}
                              >
                                {a.label}
                              </button>
                              {/* biome-ignore lint/a11y/useMediaCaption: dev tool, phoneme audio */}
                              <audio src={a.url} controls className="h-8 flex-1" />
                              <button
                                type="button"
                                onClick={() => scoreExisting(a.id, a.url)}
                                disabled={busy.has(a.id)}
                                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs text-white hover:bg-primary-700 disabled:opacity-50"
                              >
                                {busy.has(a.id) ? "Scoring…" : "Score"}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeAudio(a.id)}
                                disabled={busy.has(a.id)}
                                className="rounded-md border border-red-300 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:hover:bg-red-950"
                              >
                                Remove
                              </button>
                            </div>

                            {/* Speaker chips — one letter per known speaker (global, across
                                every phoneme), highlighted when this clip is already labelled
                                with it, so the same person can be picked consistently for a
                                given letter no matter which phoneme is open. */}
                            <div className="mt-2 flex items-end gap-1">
                              {speakerLetterPool.map((letter) => {
                                const isSelected = speakerLetter(a.speaker) === letter;
                                const sampleUrl = speakerSampleUrlByLetter.get(letter);
                                return (
                                  <div key={letter} className="flex flex-col items-center gap-0.5">
                                    {/* Preview-only, above the letter — does NOT assign, so you
                                          can listen through every speaker before picking one. A
                                          spacer keeps columns aligned when a letter has no sample yet. */}
                                    {sampleUrl ? (
                                      <button
                                        type="button"
                                        onClick={() => playSpeakerSample(sampleUrl)}
                                        title={`Play a sample of ${speakerLabelForLetter(letter)}`}
                                        className="flex h-5 w-7 items-center justify-center rounded border border-gray-300 text-[10px] text-gray-500 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600 dark:border-gray-700 dark:hover:bg-primary-900/20"
                                      >
                                        ▶
                                      </button>
                                    ) : (
                                      <div className="h-5 w-7" />
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleAudioSpeakerLetter(a.id, a.speaker, letter)
                                      }
                                      title={`${speakerLabelForLetter(letter)} — click to ${isSelected ? "unassign" : "assign"}`}
                                      className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold transition-colors ${
                                        isSelected
                                          ? "border-primary-600 bg-primary-600 text-white"
                                          : "border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 dark:border-gray-700 dark:text-gray-400"
                                      }`}
                                    >
                                      {letter}
                                    </button>
                                  </div>
                                );
                              })}
                              {speakerLetterPool.length < 26 && (
                                <button
                                  type="button"
                                  onClick={() => setExtraSpeakerLetters((n) => n + 1)}
                                  title="Reveal another speaker letter"
                                  className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-gray-300 text-xs text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:border-gray-700"
                                >
                                  +
                                </button>
                              )}
                            </div>
                            {audioResult && <ResultCard result={audioResult} />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Own voice */}
                <section>
                  <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Your voice
                  </h2>
                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={recorder.recording ? recorder.stop : recorder.start}
                        className={`rounded-md px-3 py-1.5 text-xs text-white ${
                          recorder.recording
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-gray-700 hover:bg-gray-800"
                        }`}
                      >
                        {recorder.recording ? "Stop" : "Record"}
                      </button>
                      {recorder.blobUrl && (
                        // biome-ignore lint/a11y/useMediaCaption: dev tool, own recording
                        <audio src={recorder.blobUrl} controls className="h-8 flex-1" />
                      )}
                      <button
                        type="button"
                        onClick={scoreOwnVoice}
                        disabled={!recorder.blobUrl || busy.has("own-voice")}
                        className="rounded-md bg-primary-600 px-3 py-1.5 text-xs text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {busy.has("own-voice") ? "Scoring…" : "Score"}
                      </button>
                    </div>
                    {ownVoiceResult && <ResultCard result={ownVoiceResult} />}
                  </div>
                </section>

                {/* Upload a clip — score it, keep it only if saved as a reference recording */}
                <section>
                  <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Upload audio
                  </h2>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setUploadDragOver(true);
                    }}
                    onDragLeave={() => setUploadDragOver(false)}
                    onDrop={dropUpload}
                    className={`rounded-lg border p-3 transition-colors ${
                      uploadDragOver
                        ? "border-primary-500 border-dashed bg-primary-50/60 dark:bg-primary-900/20"
                        : "border-gray-200 dark:border-gray-800"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="cursor-pointer rounded-md bg-gray-700 px-3 py-1.5 text-xs text-white hover:bg-gray-800">
                        Choose file
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) pickUpload(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {!uploadClipUrl && (
                        <span className="text-xs text-gray-400">or drag an audio file here</span>
                      )}
                      {uploadClipUrl && (
                        // biome-ignore lint/a11y/useMediaCaption: dev tool, uploaded clip
                        <audio src={uploadClipUrl} controls className="h-8 flex-1" />
                      )}
                      {uploadClipUrl && (
                        <>
                          <button
                            type="button"
                            onClick={scoreUpload}
                            disabled={busy.has("upload")}
                            className="rounded-md bg-primary-600 px-3 py-1.5 text-xs text-white hover:bg-primary-700 disabled:opacity-50"
                          >
                            {busy.has("upload") ? "Scoring…" : "Score"}
                          </button>
                          <button
                            type="button"
                            onClick={saveUploadAsReference}
                            disabled={uploadingReference}
                            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {uploadingReference ? "Saving…" : "Save as reference audio"}
                          </button>
                          <button
                            type="button"
                            onClick={clearUpload}
                            className="rounded-md px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            Discard
                          </button>
                        </>
                      )}
                    </div>
                    {uploadSaved && (
                      <p className="mt-2 text-xs text-emerald-600">
                        Saved to this phoneme's reference audio.
                      </p>
                    )}
                    {uploadResult && <ResultCard result={uploadResult} />}
                  </div>
                </section>

                {/* Diagnostics — collapsible group; inside, one panel open at a time */}
                <details className="rounded-lg border border-gray-200 dark:border-gray-800">
                  <summary className="cursor-pointer list-none p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Diagnostics
                  </summary>
                  <div className="flex flex-col gap-2 border-t border-gray-100 p-3 dark:border-gray-800">
                    <VcFrameTest
                      targetId={selected.id}
                      targetGrapheme={selected.phoneme}
                      targetIpa={selected.azureReference?.targetIPA ?? [selected.phoneme]}
                    />
                    <RecognitionProbe
                      targetId={selected.id}
                      targetGrapheme={selected.phoneme}
                      candidates={phonemes
                        .filter((p) => p.audioCount > 0)
                        .map((p) => ({
                          id: p.id,
                          phoneme: p.phoneme,
                          phonemeType: p.phonemeType,
                          audioCount: p.audioCount,
                        }))}
                    />
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddPhonemeModal
        isOpen={showAddPhoneme}
        onClose={() => {
          setShowAddPhoneme(false);
          phonemesQuery.refetch();
        }}
      />
    </div>
  );
}

export const Route = createFileRoute("/admin/phoneme-lab/")({
  component: PhonemeLabPage,
});
